import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../redux/store';
import { setIsOpen, setPersistedCategories } from '../../redux/slices/chatbotSlice';
import { useGetFAQCategoriesQuery, useGetChatbotSettingsQuery } from '../../redux/api/chatbotApi';
import { FAQItem } from '../../redux/types';
import { fullname } from '../../utils/data';
import { Message } from './ChatbotTypes';

export const useChatbotLogic = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isOpen = useSelector((state: RootState) => state.chatbot.isOpen);
  const persistedCategories = useSelector((state: RootState) => state.chatbot.persistedCategories);

  let chatbotType: 'subscriber' | 'technology' = 'subscriber';

  const { currentData: queryData, isLoading, isFetching, isError } = useGetFAQCategoriesQuery(chatbotType);

  const apiCategories = useMemo(() => {
    if (!queryData) return undefined;
    if (Array.isArray(queryData)) return queryData;
    return queryData.categories || [];
  }, [queryData]);

  const suggestedQuestions = useMemo(() => {
    if (!queryData) return [];
    if (Array.isArray(queryData)) return [];
    return queryData.suggested_questions || [];
  }, [queryData]);

  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const navEntries = performance.getEntriesByType("navigation");
      const isReload = navEntries.length > 0 && (navEntries[0] as PerformanceNavigationTiming).type === "reload";
      
      if (isReload) {
        localStorage.removeItem('clara_chat_messages_subscriber');
        return [];
      }

      const saved = localStorage.getItem('clara_chat_messages_subscriber');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Failed to parse chat messages", e);
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('clara_chat_messages_subscriber', JSON.stringify(messages));
  }, [messages]);

  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatbotRef = useRef<HTMLDivElement>(null);
  const hasGreetedRef = useRef<string | null>(null);
  const [currentSource, setCurrentSource] = useState<string | null>(null);

  const [inputValue, setInputValue] = useState('');

  const currentUser = {
    fullname: localStorage.getItem(fullname) || 'User',
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    if (Array.isArray(apiCategories) && apiCategories.length > 0) {
      dispatch(setPersistedCategories({ type: chatbotType, data: apiCategories }));
    }
  }, [apiCategories, dispatch, chatbotType]);

  const displayCategories = Array.isArray(apiCategories)
    ? apiCategories
    : (Array.isArray(persistedCategories[chatbotType]) ? persistedCategories[chatbotType] : []);

  const legacySources = useMemo(() => {
    const sources = new Set<string>();
    displayCategories.forEach(cat => {
      cat.faqs.forEach(faq => {
        if (faq.legacy_source) sources.add(faq.legacy_source);
      });
    });
    return Array.from(sources);
  }, [displayCategories]);

  const addBotMessage = (text: string, options?: Message['options']) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { type: 'bot', text, options }]);
    }, 800);
  };

  const addUserMessage = (text: string) => {
    setMessages(prev => {
      const updated = [...prev];
      if (updated.length > 0) {
        const lastMsg = updated[updated.length - 1];
        if (lastMsg.type === 'bot') {
          updated[updated.length - 1] = { ...lastMsg, options: undefined };
        }
      }
      return [...updated, { type: 'user', text }];
    });
  };

  const { data: chatbotSettings, isLoading: isLoadingSettings } = useGetChatbotSettingsQuery();

  const subscriberGreetingPhrases = [
    "Hello! I'm your EmployPro assistant. How can I help you today?",
    "Hi there! How can I assist you with your interview forms today?",
    "Welcome to EmployPro Support. What information can I help you find?",
    "Greetings! I'm here to help. What are you looking for today?"
  ];

  const getRandomGreeting = () => {
    let phrases = subscriberGreetingPhrases;
    
    if (chatbotSettings) {
      const settingsData = Array.isArray(chatbotSettings) ? chatbotSettings[0] : chatbotSettings;
      if (settingsData && settingsData.introductions) {
        const typeMatchedIntros = settingsData.introductions
          .filter((intro: any) => intro.chatbot_type === chatbotType)
          .map((intro: any) => intro.text);
          
        if (typeMatchedIntros.length > 0) {
          phrases = typeMatchedIntros;
        }
      }
    }
    
    return phrases[Math.floor(Math.random() * phrases.length)];
  };

  // Initial Greeting with Sources
  useEffect(() => {
    if (isOpen && messages.length === 0 && !isLoading && !isLoadingSettings && hasGreetedRef.current !== chatbotType) {
      hasGreetedRef.current = chatbotType;

      const initialOptions: Message['options'] = [
        ...legacySources.map(s => ({ label: s, value: s, type: 'source' as const, variant: 'source' as const }))
      ];

      addBotMessage(getRandomGreeting(), initialOptions);
    }
  }, [isOpen, messages.length, isLoading, isLoadingSettings, legacySources, suggestedQuestions, chatbotType, chatbotSettings]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, isOpen]);

  // Dynamically update the last bot message's options if it is showing sources or FAQs
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 0) return prev;
      
      const lastMsg = prev[prev.length - 1];
      if (lastMsg.type !== 'bot' || !lastMsg.options) return prev;

      if (!currentSource) {
        // We are at the root, showing sources
        const isShowingSources = lastMsg.options.some(opt => opt.type === 'source' && opt.variant !== 'back_source' && opt.variant !== 'back_question');
        
        if (isShowingSources) {
          const updatedOptions: Message['options'] = [
            ...legacySources.map(s => ({ label: s, value: s, type: 'source' as const, variant: 'source' as const }))
          ];
          
          const oldLabels = lastMsg.options.map(o => o.label).join('|');
          const newLabels = updatedOptions.map(o => o.label).join('|');
          
          if (oldLabels !== newLabels) {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = { ...lastMsg, options: updatedOptions };
            return newMessages;
          }
        }
      } else {
        // We are inside a category, showing FAQs
        const isShowingFaqs = lastMsg.options.some(opt => opt.type === 'faq');
        
        if (isShowingFaqs) {
          const faqs: FAQItem[] = [];
          displayCategories.forEach(cat => {
            cat.faqs.forEach(faq => {
              if (faq.legacy_source === currentSource) faqs.push(faq);
            });
          });
          
          const updatedOptions: Message['options'] = faqs.map(f => ({ label: f.question, value: f, type: 'faq' as const, variant: 'faq' as const }));
          if (faqs.length > 0) {
            updatedOptions.push({ label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const });
          }
          
          const oldLabels = lastMsg.options.map(o => o.label).join('|');
          const newLabels = updatedOptions.map(o => o.label).join('|');
          
          if (oldLabels !== newLabels) {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = { ...lastMsg, options: updatedOptions };
            return newMessages;
          }
        }
      }
      
      return prev;
    });
  }, [legacySources, displayCategories, currentSource]);

  // State is now persisted via localStorage, so we no longer reset on navigation.

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && chatbotRef.current && !chatbotRef.current.contains(event.target as Node)) {
        dispatch(setIsOpen(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, dispatch]);

  const followUpPhrases = [
    "Is there anything else I can assist you with?",
    "May I help you with something else?",
    "Do you need information on any other topic?",
    "How else can I help you today?",
    "Would you like to explore another category?"
  ];

  const getRandomFollowUp = () => followUpPhrases[Math.floor(Math.random() * followUpPhrases.length)];

  const fallbackPhrases = [
    "I'm sorry, I couldn't find an exact match for that question. Please select from the approved topics below, or type a different query in the search bar below.",
    "I couldn't find an approved answer for that specific phrasing. Please choose a topic below, or try typing a different query in the search bar below.",
    "I didn't quite catch that. You can select one of the help options below, or type a different question in the search bar below.",
    "Hmm, I don't have an approved answer for that keyword. Try selecting from the topics listed below or type your query in the search bar below."
  ];

  const getRandomFallback = () => fallbackPhrases[Math.floor(Math.random() * fallbackPhrases.length)];

  const getSourceSelectionMessage = (name: string) => {
    const phrases = [
      `Please select one of the options related to ${name}:`,
      `I've found the following topics for ${name}. Which one would you like to explore?`,
      `Here are the available help topics regarding ${name}:`,
      `How can I help you with ${name}? Please choose a question below:`,
      `Regarding ${name}, I can provide information on these topics:`
    ];
    return phrases[Math.floor(Math.random() * phrases.length)];
  };

  const handleOptionClick = (option: { label: string; value: any; type: 'source' | 'faq' }) => {
    addUserMessage(option.label);

    if (option.value === 'back') {
      setCurrentSource(null);

      const initialOptions: Message['options'] = [
        // ...(chatbotType === 'technology' && suggestedQuestions.length > 0
        //   ? suggestedQuestions.map(q => ({ label: q.prompt, value: q, type: 'faq' as const, variant: 'faq' as const }))
        //   : []
        // ),
        ...legacySources.map(s => ({ label: s, value: s, type: 'source' as const, variant: 'source' as const }))
      ];

      addBotMessage(getRandomFollowUp(), initialOptions);
      return;
    }

    if (option.value === 'back_to_questions' && currentSource) {
      const faqs: FAQItem[] = [];
      displayCategories.forEach(cat => {
        cat.faqs.forEach(faq => {
          if (faq.legacy_source === currentSource) faqs.push(faq);
        });
      });
      const faqOptions: Message['options'] = faqs.map(f => ({ label: f.question, value: f, type: 'faq' as const, variant: 'faq' as const }));
      faqOptions.push({ label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const });

      addBotMessage(getSourceSelectionMessage(currentSource), faqOptions);
      return;
    }

    if (option.type === 'source') {
      const sourceName = option.value as string;
      setCurrentSource(sourceName);
      const faqs: FAQItem[] = [];
      displayCategories.forEach(cat => {
        cat.faqs.forEach(faq => {
          if (faq.legacy_source === sourceName) faqs.push(faq);
        });
      });

      const faqOptions: Message['options'] = faqs.map(f => ({ label: f.question, value: f, type: 'faq' as const, variant: 'faq' as const }));
      if (faqs.length > 0) {
        faqOptions.push({ label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const });
      }

      addBotMessage(getSourceSelectionMessage(sourceName), faqOptions);
    } else if (option.type === 'faq') {
      const isSuggested = option.value && 'prompt' in option.value && 'answer' in option.value;
      if (isSuggested) {
        const backOption = {
          label: '↩ Back to Questions',
          value: 'back',
          type: 'source' as const,
          variant: 'back_question' as const
        };
        addBotMessage(option.value.answer, [backOption]);
      } else {
        const faq = option.value as FAQItem;
        const sourceFaqsCount = displayCategories.reduce((acc, cat) =>
          acc + cat.faqs.filter(f => f.legacy_source === faq.legacy_source).length, 0
        );

        const backOption = (sourceFaqsCount > 1 && currentSource)
          ? { label: '↩ Back to Questions', value: 'back_to_questions', type: 'source' as const, variant: 'back_question' as const }
          : { label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const };

        addBotMessage(faq.answer, [backOption]);
      }
    }
  };

  const executeSearch = (query: string) => {
    const cleanQuery = query.toLowerCase().replace(/[?.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
    const queryWords = cleanQuery.split(' ').filter(w => w.length > 2);

    // Helper to calculate word overlap score
    const getMatchScore = (targetText: string) => {
      const cleanTarget = targetText.toLowerCase().replace(/[?.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
      if (cleanTarget === cleanQuery) return 100;
      if (cleanTarget.includes(cleanQuery) && cleanQuery.length > 10) return 80;
      if (cleanTarget.includes(cleanQuery)) return 50;

      const targetWords = cleanTarget.split(' ').filter(w => w.length > 2);
      let matches = 0;
      queryWords.forEach(qw => {
        if (targetWords.some(tw => tw.includes(qw) || qw.includes(tw))) matches++;
      });
      return targetWords.length > 0 ? (matches / Math.max(queryWords.length, targetWords.length)) * 100 : 0;
    };

    const matchedFaqs: { label: string; value: any; type: 'faq', variant: 'faq', score: number, legacy_source: string | null }[] = [];

    /*
    if (chatbotType === 'technology' && suggestedQuestions.length > 0) {
      for (const q of suggestedQuestions) {
        const score = getMatchScore(q.prompt);
        if (score >= 30) {
          matchedFaqs.push({ label: q.prompt, value: q, type: 'faq', variant: 'faq', score, legacy_source: null });
        }
      }
    }
    */

    const allFaqs: { faq: FAQItem; categoryName: string }[] = [];
    for (const cat of displayCategories) {
      for (const faq of cat.faqs) {
        allFaqs.push({ faq, categoryName: cat.name });
      }
    }

    for (const item of allFaqs) {
      const score = getMatchScore(item.faq.question);
      if (score >= 30) {
        matchedFaqs.push({ label: item.faq.question, value: item.faq, type: 'faq', variant: 'faq', score, legacy_source: item.faq.legacy_source });
      }
    }

    // Sort matches by highest score
    matchedFaqs.sort((a, b) => b.score - a.score);

    // 2nd PRIORITY: Category (legacy_source) matching
    let highestCategoryMatch: string | null = null;
    let highestCategoryScore = 0;

    for (const source of legacySources) {
      const sourceKeyword = source.toLowerCase().replace(/[?.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
      if (cleanQuery === sourceKeyword) {
        highestCategoryMatch = source;
        highestCategoryScore = 100;
        break; // Exact category match
      } else if (sourceKeyword.includes(cleanQuery) || cleanQuery.includes(sourceKeyword)) {
        highestCategoryMatch = source;
        highestCategoryScore = 80;
      }
    }

    // DECISION LOGIC:

    // 1. Direct Question Answer (Very Strong Match)
    if (matchedFaqs.length > 0 && matchedFaqs[0].score >= 80) {
      const bestMatch = matchedFaqs[0];
      const backOption = { label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const };
      addBotMessage(bestMatch.value.answer, [backOption]);
      return;
    }

    // 2. Exact Category Match
    if (highestCategoryMatch && highestCategoryScore === 100) {
      setCurrentSource(highestCategoryMatch);
      const faqs: FAQItem[] = [];
      displayCategories.forEach(cat => {
        cat.faqs.forEach(faq => {
          if (faq.legacy_source === highestCategoryMatch) faqs.push(faq);
        });
      });

      const faqOptions: Message['options'] = faqs.map(f => ({ label: f.question, value: f, type: 'faq' as const, variant: 'faq' as const }));
      if (faqs.length > 0) {
        faqOptions.push({ label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const });
      }

      addBotMessage(getSourceSelectionMessage(highestCategoryMatch), faqOptions);
      return;
    }

    // 3. Multiple Question Options (Generic Keyword Search like "form")
    if (matchedFaqs.length > 0) {
      // De-duplicate questions if multiple matched
      const uniqueFaqs = Array.from(new Map(matchedFaqs.map(item => [item.label, item])).values());
      const topFaqs = uniqueFaqs.slice(0, 6); // Limit to top 6 so we don't overwhelm the UI

      const options: Message['options'] = topFaqs.map(f => ({ label: f.label, value: f.value, type: 'faq' as const, variant: 'faq' as const }));
      if (options) {
        options.push({ label: '↩ Back to Sources', value: 'back', type: 'source' as const, variant: 'back_source' as const });
      }

      addBotMessage("I found these relevant questions for your search. Please choose one:", options);
      return;
    }

    // 4. Fallback
    const fallbackOptions: Message['options'] = [
      ...legacySources.map(s => ({ label: s, value: s, type: 'source' as const, variant: 'source' as const }))
    ];

    addBotMessage(getRandomFallback(), fallbackOptions);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const query = inputValue.trim();
    setInputValue('');
    addUserMessage(query);

    executeSearch(query);
  };

  return {
    dispatch,
    isOpen,
    isLoading,
    isError,
    messages,
    isTyping,
    messagesEndRef,
    chatbotRef,
    inputValue,
    setInputValue,
    handleSendMessage,
    handleOptionClick,
    currentUser,
    getUserInitials
  };
};
